import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import type {
  DeselectFare,
  FlightFareMeta,
  UseFlightFaresSource,
} from "@b2bportal/core-types";
import { compareFaresByPrice } from "@b2bportal/core-utilities";

export type FlightFareTransformFn = (
  rates: FlightFareMeta[]
) => FlightFareMeta[];

export interface FlightFareSourcesConfig {
  hooks: UseFlightFaresSource[];
  transform: FlightFareTransformFn;
  setDeselector: (deselector: DeselectFare | null) => void;
  deselect: () => void;
}

const FlightFareSourcesContext = createContext<FlightFareSourcesConfig | null>(
  null
);

const noop = () => {};

export const useFlightFareSources = (): FlightFareSourcesConfig => {
  const context = useContext(FlightFareSourcesContext);

  const emptyFlightFareSourcesConfig: FlightFareSourcesConfig = useMemo(
    () => ({
      hooks: [],
      transform: noopTransform,
      setDeselector: noop,
      deselect: noop,
    }),
    []
  );

  // If no context is found, return the empty config, for backwards compatibility
  if (context == null) {
    return emptyFlightFareSourcesConfig;
  }

  return context;
};

export const sortByPrice: FlightFareTransformFn = (
  rates: FlightFareMeta[]
): FlightFareMeta[] =>
  rates.sort((a, b) => compareFaresByPrice(a.fareDetails, b.fareDetails));

export interface FlightFareSourcesProviderProps {
  hooks: UseFlightFaresSource[];
  transform?: FlightFareTransformFn;
  children: React.ReactNode;
}

export function FlightFareSourcesProvider({
  hooks,
  transform = sortByPrice,
  children,
}: FlightFareSourcesProviderProps) {
  const parentContext = useContext(FlightFareSourcesContext);
  const initialHooksRef = useRef(hooks);
  const deselectRef = useRef<DeselectFare | null>(null);

  // Dev-only: ensure hooks never changes (hooks rules safety)
  if (import.meta.env.VITE_ENV !== "production") {
    if (
      hooks !== initialHooksRef.current ||
      hooks.length !== initialHooksRef.current.length
    ) {
      throw new Error(
        "FlightFareSourcesProvider: hooks must be static and cannot change between renders."
      );
    }
  }

  const setDeselector = useCallback((deselector: DeselectFare | null) => {
    deselectRef.current = deselector;
  }, []);

  const deselect = useCallback(() => {
    deselectRef.current?.();
    deselectRef.current = null;
  }, []);

  const value = useMemo(
    () => ({ hooks, transform, setDeselector, deselect }),
    [hooks, transform, setDeselector, deselect]
  );

  // First/top provider in the chain wins - if parent exists, pass through
  if (parentContext != null) {
    return <>{children}</>;
  }

  return (
    <FlightFareSourcesContext.Provider value={value}>
      {children}
    </FlightFareSourcesContext.Provider>
  );
}

const noopTransform: FlightFareTransformFn = (rates: FlightFareMeta[]) => rates;
